import * as THREE from 'three';

import { isDirectOpaqueId } from './direct-scene-profile';

export const DIRECT_OBJECT_MODEL_JSON_MAX_BYTES = 256 * 1024;
// Cây node và tài nguyên chỉ còn chặn theo số lượng để giữ chi phí render dự
// đoán được; hình dạng và độ lồng nhau do model tự quyết định.
export const DIRECT_OBJECT_MODEL_MAX_NODES = 256;
export const DIRECT_OBJECT_MODEL_MAX_DEPTH = 12;
export const DIRECT_OBJECT_MODEL_MAX_RESOURCES = 256;
// Geometry không còn bị giới hạn theo loại hay kích thước, chỉ còn chặn theo
// tài nguyên: độ sâu JSON, độ dài mảng và tổng số phần tử của mọi geometry.
export const DIRECT_OBJECT_MODEL_MAX_GEOMETRY_DEPTH = 6;
export const DIRECT_OBJECT_MODEL_MAX_GEOMETRY_ARRAY_LENGTH = 65_536;
export const DIRECT_OBJECT_MODEL_MAX_GEOMETRY_VALUES = 131_072;

/**
 * Bất kỳ geometry nào THREE dựng được: tham số (BoxGeometry, LatheGeometry...)
 * hoặc BufferGeometry với data/attributes. Key ngoài `uuid`/`type` chỉ cần là
 * dữ liệu an toàn — xem isSafeGeometryValue().
 */
export interface DirectObjectGeometryJSON {
  uuid: string;
  type: string;
  [key: string]: unknown;
}

export type DirectObjectMaterialType =
  | 'MeshBasicMaterial'
  | 'MeshLambertMaterial'
  | 'MeshPhongMaterial'
  | 'MeshStandardMaterial';

export interface DirectObjectMaterialJSON {
  uuid: string;
  type: DirectObjectMaterialType;
  name?: string;
  color?: number;
  emissive?: number;
  emissiveIntensity?: number;
  specular?: number;
  shininess?: number;
  roughness?: number;
  metalness?: number;
  reflectivity?: number;
  refractionRatio?: number;
  envMapIntensity?: number;
  opacity?: number;
  transparent?: boolean;
  side?: number;
  wireframe?: boolean;
  flatShading?: boolean;
  vertexColors?: boolean;
  depthTest?: boolean;
  depthWrite?: boolean;
  colorWrite?: boolean;
  alphaTest?: number;
  visible?: boolean;
  toneMapped?: boolean;
  fog?: boolean;
  depthFunc?: number;
  stencilWrite?: boolean;
  stencilWriteMask?: number;
  stencilFunc?: number;
  stencilRef?: number;
  stencilFuncMask?: number;
  stencilFail?: number;
  stencilZFail?: number;
  stencilZPass?: number;
  polygonOffset?: boolean;
  polygonOffsetFactor?: number;
  polygonOffsetUnits?: number;
}

interface DirectObjectNodeJSONBase {
  uuid: string;
  type: 'Group' | 'Mesh';
  name?: string;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
  matrix?: number[];
  layers?: number;
  visible?: boolean;
  renderOrder?: number;
  castShadow?: boolean;
  receiveShadow?: boolean;
  frustumCulled?: boolean;
}

export interface DirectObjectGroupJSON extends DirectObjectNodeJSONBase {
  type: 'Group';
  children?: DirectObjectNodeJSON[];
}

export interface DirectObjectMeshJSON extends DirectObjectNodeJSONBase {
  type: 'Mesh';
  geometry: string;
  material: string;
  children?: DirectObjectNodeJSON[];
}

export type DirectObjectNodeJSON = DirectObjectGroupJSON | DirectObjectMeshJSON;

export interface DirectObjectModelJSON {
  metadata: {
    version: 4.5;
    type: 'Object';
    generator?: string;
  };
  geometries: DirectObjectGeometryJSON[];
  materials: DirectObjectMaterialJSON[];
  object: DirectObjectGroupJSON;
}

const TOP_LEVEL_KEYS = ['metadata', 'geometries', 'materials', 'object'];
const METADATA_KEYS = ['version', 'type', 'generator'];
const MATERIAL_COMMON_KEYS = [
  'uuid',
  'type',
  'name',
  'color',
  'opacity',
  'transparent',
  'side',
  'wireframe',
  'flatShading',
  'vertexColors',
  'depthTest',
  'depthWrite',
  'colorWrite',
  'alphaTest',
  'visible',
  'toneMapped',
  'fog',
  'reflectivity',
  'refractionRatio',
  'envMapIntensity',
  'depthFunc',
  'stencilWrite',
  'stencilWriteMask',
  'stencilFunc',
  'stencilRef',
  'stencilFuncMask',
  'stencilFail',
  'stencilZFail',
  'stencilZPass',
  'polygonOffset',
  'polygonOffsetFactor',
  'polygonOffsetUnits',
];
const MATERIAL_TYPE_KEYS: Record<DirectObjectMaterialType, string[]> = {
  MeshBasicMaterial: [],
  MeshLambertMaterial: ['emissive', 'emissiveIntensity'],
  MeshPhongMaterial: ['emissive', 'emissiveIntensity', 'specular', 'shininess'],
  MeshStandardMaterial: [
    'emissive',
    'emissiveIntensity',
    'roughness',
    'metalness',
  ],
};
const NODE_COMMON_KEYS = [
  'uuid',
  'type',
  'name',
  'position',
  'rotation',
  'scale',
  'matrix',
  'layers',
  'visible',
  'renderOrder',
  'castShadow',
  'receiveShadow',
  'frustumCulled',
];
const GROUP_KEYS = [...NODE_COMMON_KEYS, 'children'];
const MESH_KEYS = [...NODE_COMMON_KEYS, 'children', 'geometry', 'material'];
// Key bị cấm ở mọi nơi nhận key tự do: JSON.parse tạo chúng thành own property
// và THREE có thể copy sang object thật (prototype pollution).
const RESERVED_KEYS = ['__proto__', 'constructor', 'prototype'];
const GEOMETRY_TYPE_PATTERN = /^[A-Za-z][A-Za-z0-9]{0,63}$/;
const GEOMETRY_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
// Geometry chỉ mô tả hình học; mọi chuỗi trỏ ra tài nguyên ngoài đều bị loại.
const EXTERNAL_RESOURCE_PATTERN = /:\/\/|^\s*(data|blob|javascript|file):/i;
const MATERIAL_TYPES = new Set<DirectObjectMaterialType>([
  'MeshBasicMaterial',
  'MeshLambertMaterial',
  'MeshPhongMaterial',
  'MeshStandardMaterial',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
): boolean {
  return Object.keys(value).every(key => allowedKeys.includes(key));
}

function isUuid(value: unknown): value is string {
  return isDirectOpaqueId(value);
}

function registerUuid(value: unknown, uuids: Set<string>): value is string {
  if (!isUuid(value) || uuids.has(value)) return false;
  uuids.add(value);
  return true;
}

function isOptionalName(value: unknown): boolean {
  return (
    value === undefined || (typeof value === 'string' && value.length <= 128)
  );
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isOptionalFinite(value: unknown): boolean {
  return value === undefined || isFiniteNumber(value);
}

function isOptionalBoolean(value: unknown): boolean {
  return value === undefined || typeof value === 'boolean';
}

function isNumberTuple(value: unknown): value is [number, number, number] {
  return (
    Array.isArray(value) && value.length === 3 && value.every(isFiniteNumber)
  );
}

function isOptionalNumberTuple(value: unknown): boolean {
  return value === undefined || isNumberTuple(value);
}

function isOptionalMatrix(value: unknown): boolean {
  return (
    value === undefined ||
    (Array.isArray(value) && value.length === 16 && value.every(isFiniteNumber))
  );
}

function isOptionalUnsignedInteger(value: unknown): boolean {
  return (
    value === undefined ||
    (typeof value === 'number' &&
      Number.isInteger(value) &&
      value >= 0 &&
      value <= 0xffffffff)
  );
}

function isOptionalColor(value: unknown): boolean {
  return (
    value === undefined ||
    (typeof value === 'number' &&
      Number.isInteger(value) &&
      value >= 0x000000 &&
      value <= 0xffffff)
  );
}

function isOptionalUnitInterval(value: unknown): boolean {
  return (
    value === undefined || (isFiniteNumber(value) && value >= 0 && value <= 1)
  );
}

function isValidMetadata(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, METADATA_KEYS) &&
    value['version'] === 4.5 &&
    value['type'] === 'Object' &&
    (value['generator'] === undefined ||
      (typeof value['generator'] === 'string' &&
        value['generator'].length > 0 &&
        value['generator'].length <= 128))
  );
}

/**
 * Geometry budget dùng chung cho cả model: tổng số phần tử mảng của mọi
 * geometry, để một payload hợp lệ vẫn không thể phình tài nguyên.
 */
interface GeometryBudget {
  values: number;
}

function isSafeGeometryKey(key: string): boolean {
  return GEOMETRY_KEY_PATTERN.test(key) && !RESERVED_KEYS.includes(key);
}

function isSafeGeometryString(value: string): boolean {
  return value.length <= 128 && !EXTERNAL_RESOURCE_PATTERN.test(value);
}

/** Chỉ chấp nhận dữ liệu thuần: số hữu hạn, boolean, chuỗi ngắn, mảng, record. */
function isSafeGeometryValue(
  value: unknown,
  depth: number,
  budget: GeometryBudget,
): boolean {
  if (value === null || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') return isSafeGeometryString(value);
  if (depth >= DIRECT_OBJECT_MODEL_MAX_GEOMETRY_DEPTH) return false;

  if (Array.isArray(value)) {
    if (value.length > DIRECT_OBJECT_MODEL_MAX_GEOMETRY_ARRAY_LENGTH) {
      return false;
    }
    budget.values += value.length;
    if (budget.values > DIRECT_OBJECT_MODEL_MAX_GEOMETRY_VALUES) return false;
    return value.every(item => isSafeGeometryValue(item, depth + 1, budget));
  }

  if (!isRecord(value)) return false;
  return Object.keys(value).every(
    key =>
      isSafeGeometryKey(key) &&
      isSafeGeometryValue(value[key], depth + 1, budget),
  );
}

/**
 * Bất kỳ geometry nào THREE.ObjectLoader dựng được: `BufferGeometry` /
 * `InstancedBufferGeometry` đi qua BufferGeometryLoader, các lớp kế thừa
 * BufferGeometry đi qua `fromJSON`. Loại lạ bị chặn tại đây thay vì để
 * ObjectLoader ném lỗi giữa chừng và bỏ lại tài nguyên đã tạo.
 */
function isSupportedGeometryType(value: unknown): value is string {
  if (typeof value !== 'string' || !GEOMETRY_TYPE_PATTERN.test(value)) {
    return false;
  }

  const candidate = (THREE as unknown as Record<string, unknown>)[value];
  if (typeof candidate !== 'function') return false;
  return (
    candidate === THREE.BufferGeometry ||
    candidate.prototype instanceof THREE.BufferGeometry
  );
}

function isValidGeometry(
  value: unknown,
  uuids: Set<string>,
  budget: GeometryBudget,
): value is DirectObjectGeometryJSON {
  if (
    !isRecord(value) ||
    !registerUuid(value['uuid'], uuids) ||
    !isSupportedGeometryType(value['type'])
  )
    return false;

  // Không giới hạn kích thước, số segment hay loại hình học: chỉ cần dữ liệu
  // an toàn và nằm trong budget tài nguyên.
  return Object.keys(value).every(
    key =>
      key === 'uuid' ||
      key === 'type' ||
      (isSafeGeometryKey(key) && isSafeGeometryValue(value[key], 1, budget)),
  );
}

function isValidMaterial(
  value: unknown,
  uuids: Set<string>,
): value is DirectObjectMaterialJSON {
  if (
    !isRecord(value) ||
    !MATERIAL_TYPES.has(value['type'] as DirectObjectMaterialType)
  )
    return false;

  const type = value['type'] as DirectObjectMaterialType;
  if (
    !hasOnlyKeys(value, [
      ...MATERIAL_COMMON_KEYS,
      ...MATERIAL_TYPE_KEYS[type],
    ]) ||
    !registerUuid(value['uuid'], uuids) ||
    !isOptionalName(value['name']) ||
    !isOptionalColor(value['color']) ||
    !isOptionalColor(value['emissive']) ||
    !isOptionalColor(value['specular']) ||
    !isOptionalUnitInterval(value['opacity']) ||
    !isOptionalUnitInterval(value['alphaTest']) ||
    !isOptionalUnitInterval(value['roughness']) ||
    !isOptionalUnitInterval(value['metalness']) ||
    !isOptionalFinite(value['reflectivity']) ||
    !isOptionalFinite(value['refractionRatio']) ||
    !isOptionalFinite(value['envMapIntensity']) ||
    !isOptionalBoolean(value['transparent']) ||
    !isOptionalBoolean(value['wireframe']) ||
    !isOptionalBoolean(value['flatShading']) ||
    !isOptionalBoolean(value['vertexColors']) ||
    !isOptionalBoolean(value['depthTest']) ||
    !isOptionalBoolean(value['depthWrite']) ||
    !isOptionalBoolean(value['colorWrite']) ||
    !isOptionalBoolean(value['visible']) ||
    !isOptionalBoolean(value['toneMapped']) ||
    !isOptionalBoolean(value['fog']) ||
    !isOptionalBoolean(value['stencilWrite']) ||
    !isOptionalUnsignedInteger(value['depthFunc']) ||
    !isOptionalUnsignedInteger(value['stencilWriteMask']) ||
    !isOptionalUnsignedInteger(value['stencilFunc']) ||
    !isOptionalUnsignedInteger(value['stencilRef']) ||
    !isOptionalUnsignedInteger(value['stencilFuncMask']) ||
    !isOptionalUnsignedInteger(value['stencilFail']) ||
    !isOptionalUnsignedInteger(value['stencilZFail']) ||
    !isOptionalUnsignedInteger(value['stencilZPass']) ||
    !isOptionalBoolean(value['polygonOffset']) ||
    (value['side'] !== undefined &&
      (!Number.isInteger(value['side']) ||
        (value['side'] as number) < 0 ||
        (value['side'] as number) > 2)) ||
    (value['shininess'] !== undefined &&
      (!isFiniteNumber(value['shininess']) || value['shininess'] < 0)) ||
    (value['emissiveIntensity'] !== undefined &&
      (!isFiniteNumber(value['emissiveIntensity']) ||
        value['emissiveIntensity'] < 0))
  )
    return false;

  const factor = value['polygonOffsetFactor'];
  const units = value['polygonOffsetUnits'];
  if (factor === undefined && units === undefined) return true;
  return (
    value['polygonOffset'] === true &&
    isFiniteNumber(factor) &&
    isFiniteNumber(units)
  );
}

interface TreeValidationContext {
  uuids: Set<string>;
  geometryUuids: Set<string>;
  materialUuids: Set<string>;
  referencedGeometryUuids: Set<string>;
  referencedMaterialUuids: Set<string>;
  nodeCount: number;
  meshCount: number;
}

function hasValidNodeProperties(value: Record<string, unknown>): boolean {
  return (
    isOptionalName(value['name']) &&
    isOptionalNumberTuple(value['position']) &&
    isOptionalNumberTuple(value['rotation']) &&
    isOptionalNumberTuple(value['scale']) &&
    isOptionalMatrix(value['matrix']) &&
    isOptionalUnsignedInteger(value['layers']) &&
    isOptionalBoolean(value['visible']) &&
    isOptionalBoolean(value['castShadow']) &&
    isOptionalBoolean(value['receiveShadow']) &&
    isOptionalBoolean(value['frustumCulled']) &&
    (value['renderOrder'] === undefined ||
      (isFiniteNumber(value['renderOrder']) && value['renderOrder'] >= 0))
  );
}

function isValidNode(
  value: unknown,
  depth: number,
  context: TreeValidationContext,
): value is DirectObjectNodeJSON {
  if (
    !isRecord(value) ||
    depth > DIRECT_OBJECT_MODEL_MAX_DEPTH ||
    ++context.nodeCount > DIRECT_OBJECT_MODEL_MAX_NODES ||
    !registerUuid(value['uuid'], context.uuids) ||
    !hasValidNodeProperties(value)
  )
    return false;

  if (value['type'] === 'Group') {
    return (
      hasOnlyKeys(value, GROUP_KEYS) &&
      hasValidChildren(value['children'], depth, context)
    );
  }

  if (
    value['type'] !== 'Mesh' ||
    !hasOnlyKeys(value, MESH_KEYS) ||
    !isUuid(value['geometry']) ||
    !context.geometryUuids.has(value['geometry']) ||
    !isUuid(value['material']) ||
    !context.materialUuids.has(value['material'])
  )
    return false;

  context.meshCount += 1;
  context.referencedGeometryUuids.add(value['geometry']);
  context.referencedMaterialUuids.add(value['material']);
  // Mesh cũng được phép có child: object lồng nhau là hợp lệ.
  return hasValidChildren(value['children'], depth, context);
}

function hasValidChildren(
  children: unknown,
  depth: number,
  context: TreeValidationContext,
): boolean {
  if (children === undefined) return true;
  return (
    Array.isArray(children) &&
    children.every(child => isValidNode(child, depth + 1, context))
  );
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

/**
 * Validate the ObjectLoader subset accepted from direct Python object events.
 *
 * Shape is intentionally open: one Group root, arbitrary nesting of Group/Mesh
 * nodes with transforms, and any geometry THREE can build at any size. What
 * stays closed is everything outside geometry/material data — external
 * resources, executable scene features (lights, cameras, animations,
 * skeletons) and unbounded payloads — before data reaches THREE.ObjectLoader.
 */
export function parseDirectObjectModelJSON(
  modelJSON: unknown,
): DirectObjectModelJSON | null {
  if (
    typeof modelJSON !== 'string' ||
    utf8ByteLength(modelJSON) > DIRECT_OBJECT_MODEL_JSON_MAX_BYTES
  )
    return null;

  let value: unknown;
  try {
    value = JSON.parse(modelJSON);
  } catch (_error) {
    return null;
  }

  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, TOP_LEVEL_KEYS) ||
    !isValidMetadata(value['metadata']) ||
    !Array.isArray(value['geometries']) ||
    value['geometries'].length < 1 ||
    value['geometries'].length > DIRECT_OBJECT_MODEL_MAX_RESOURCES ||
    !Array.isArray(value['materials']) ||
    value['materials'].length < 1 ||
    value['materials'].length > DIRECT_OBJECT_MODEL_MAX_RESOURCES ||
    !isRecord(value['object']) ||
    value['object']['type'] !== 'Group'
  )
    return null;

  const uuids = new Set<string>();
  const geometryBudget: GeometryBudget = { values: 0 };
  if (
    !value['geometries'].every(item =>
      isValidGeometry(item, uuids, geometryBudget),
    )
  )
    return null;
  if (!value['materials'].every(item => isValidMaterial(item, uuids)))
    return null;

  const context: TreeValidationContext = {
    uuids,
    geometryUuids: new Set(value['geometries'].map(geometry => geometry.uuid)),
    materialUuids: new Set(value['materials'].map(material => material.uuid)),
    referencedGeometryUuids: new Set<string>(),
    referencedMaterialUuids: new Set<string>(),
    nodeCount: 0,
    meshCount: 0,
  };
  if (
    !isValidNode(value['object'], 0, context) ||
    context.meshCount < 1 ||
    context.referencedGeometryUuids.size !== context.geometryUuids.size ||
    context.referencedMaterialUuids.size !== context.materialUuids.size
  )
    return null;

  return value as unknown as DirectObjectModelJSON;
}
